import * as fromGradesActions from "#core/teacher-classes/store/actions/grades.action";
import { AssignmentPagination } from "@carnegie-learning/data-access-angular-passport";
import { Action, createReducer, on } from "@ngrx/store";
import { PPAssignment as Assignment } from "cli-types";

export interface GradesState {
    entities: Map<number, Assignment>;
    currentAssignment: Assignment | null;
    quickEval: boolean;
    loaded: boolean;
    loading: boolean;
    entitiesLoaded: boolean;
    entitiesLoading: boolean;
    rawResponse: AssignmentPagination | null;
    updateStudentAssignmentFailure: boolean;
    updateStudentAssignmentSuccess: boolean;
}

export const initialState: GradesState = {
    entities: new Map<number, Assignment>(),
    currentAssignment: null,
    quickEval: false,
    loaded: false,
    loading: false,
    entitiesLoaded: false,
    entitiesLoading: false,
    rawResponse: null,
    updateStudentAssignmentFailure: false,
    updateStudentAssignmentSuccess: false,
};

const gradesReducer = createReducer(
    initialState,
    on(fromGradesActions.LoadCurrentStudentAssignment, (state, {}) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(
        fromGradesActions.LoadCurrentStudentAssignmentSuccess,
        (state, { payload }) => {
            return {
                ...state,
                loading: false,
                loaded: true,
                currentAssignment: payload,
            };
        }
    ),
    on(
        fromGradesActions.LoadAllGradeAssignmentAssetsSuccess,
        (state, { assignments, assets }) => {
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            let updatedCurrentAssignment = state.currentAssignment;

            if (assignments && assets) {
                assignments.forEach((assignment) => {
                    const asset = assets.find(
                        (a) => a.assetId === assignment.assetId
                    );
                    assignment = { ...assignment, asset: asset };

                    if (
                        assignment.compositeTaskAssignments &&
                        assignment.compositeTaskAssignments.length > 0
                    ) {
                        const newCompositeTaskAssignments = assignment.compositeTaskAssignments.map(
                            (task) => {
                                const asset2 = assets.find(
                                    (a) => a.assetId === task.assetId
                                );
                                return { ...task, asset: asset2 };
                            }
                        );
                        if (newCompositeTaskAssignments) {
                            assignment = {
                                ...assignment,
                                compositeTaskAssignments: newCompositeTaskAssignments,
                            };
                        }
                    }
                    if (
                        updatedCurrentAssignment &&
                        updatedCurrentAssignment.assignmentID ===
                            assignment.assignmentID
                    ) {
                        updatedCurrentAssignment = { ...assignment };
                    }

                    const currentEntity = entities.get(assignment.assignmentID); // current assignment should retain the student information
                    if (currentEntity) {
                        assignment = { ...currentEntity, ...assignment };
                    }

                    entities.set(assignment.assignmentID, assignment);
                });
            }

            return {
                ...state,
                entities: entities,
                currentAssignment: updatedCurrentAssignment,
            };
        }
    ),
    on(fromGradesActions.SetCurrentStudentAssignment, (state, { payload }) => {
        return {
            ...state,
            currentAssignment: payload,
        };
    }),
    on(fromGradesActions.LoadCurrentStudentAssignmentFail, (state, {}) => {
        return {
            ...state,
            loading: false,
            loaded: false,
        };
    }),
    on(fromGradesActions.LoadAssignmentList, (state, {}) => {
        return {
            ...state,
            entitiesLoading: true,
            entitiesLoaded: false,
        };
    }),
    on(fromGradesActions.LoadAssignmentListSuccess, (state, { payload }) => {
        const assignments = (<AssignmentPagination>payload).data;

        const entities = new Map<number, Assignment>();
        assignments.forEach((assignment) => {
            entities.set(assignment.assignmentID, assignment);
        });

        // then re-bind the entity const with our returned state
        return {
            ...state,
            entitiesLoading: false,
            entitiesLoaded: true,
            entities,
            rawResponse: payload,
        };
    }),
    on(fromGradesActions.LoadAssignmentListFail, (state, {}) => {
        return {
            ...state,
            entitiesLoading: false,
            entitiesLoaded: false,
        };
    }),
    on(
        fromGradesActions.UpdateStudentAssignmentSuccess,
        (state, { payload, quickEval }) => {
            const assignment = payload;
            state = { ...state, updateStudentAssignmentSuccess: true };

            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            // Need to keep appended keys since return payload does not contain they properties
            let currentEntity = entities.get(assignment.assignmentID);
            currentEntity = Object.assign({}, currentEntity, assignment); // merge the two without missing properties

            entities.set(assignment.assignmentID, currentEntity);
            if (quickEval) {
                entities.delete(assignment.assignmentID);
            }

            if (
                state.currentAssignment &&
                state.currentAssignment.assignmentID === assignment.assignmentID
            ) {
                return {
                    ...state,
                    entities,
                    currentAssignment: {
                        ...state.currentAssignment,
                        ...assignment,
                    },
                };
            } else if (!state.currentAssignment && assignment) {
                return {
                    ...state,
                    entities,
                    currentAssignment: {
                        ...assignment,
                    },
                };
            } else {
                return {
                    ...state,
                    entities,
                };
            }
        }
    ),
    on(fromGradesActions.UpdateStudentAssignmentFail, (state, {}) => {
        return {
            ...state,
            updateStudentAssignmentFailure: true,
            updateStudentAssignmentSuccess: false,
        };
    }),
    on(fromGradesActions.UpdateStudentAssignment, (state, {}) => {
        return {
            ...state,
            updateStudentAssignmentSuccess: false,
        };
    }),
    on(fromGradesActions.ResetGradesStore, (state, {}) => {
        return initialState;
    })
);

export function reducer(state: GradesState | undefined, action: Action) {
    return gradesReducer(state, action);
}

// this is where we want to export some levels of state
export const getGradesEntities = (state: GradesState) => state.entities;
export const getCurrentAssignment = (state: GradesState) =>
    state.currentAssignment;
export const getGradesLoading = (state: GradesState) => state.entitiesLoading;
export const getGradesLoaded = (state: GradesState) => state.entitiesLoaded;
export const getGradesEntitiesLoading = (state: GradesState) =>
    state.entitiesLoading;
export const getGradesEntitiesLoaded = (state: GradesState) =>
    state.entitiesLoaded;
export const getRawResponse = (state: GradesState) => state.rawResponse;
export const updateStudentAssignmentFailure = (state: GradesState) =>
    state.updateStudentAssignmentFailure;
export const updateStudentAssignmentSuccess = (state: GradesState) =>
    state.updateStudentAssignmentSuccess;
